import { memo, useEffect, useMemo, useState } from "react";
import {
  Node,
  useNodeConnections,
  useNodesData,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  LogicalOperator,
  Outcome,
} from "@unlikelyai-magic/shared/data-access/api";
import { Paragraph } from "@unlikelyai-magic/ui/typography";
import { RFNodeData, evaluateAnd, evaluateNot, evaluateOr } from "../utils";
import { BaseNode } from "./BaseNode";

/**
 * Normalizes propagateWhen values to Outcome enum values.
 * Handles both string values (e.g., "TRUE") and Outcome enum values.
 */
const normalizePropagateWhen = (
  propagateWhen: Outcome[] | string[] | undefined
): Outcome[] | undefined => {
  if (!propagateWhen || !Array.isArray(propagateWhen)) return undefined;
  return propagateWhen.map((v) =>
    typeof v === "string" ? ((Outcome as any)[v] ?? v) : v
  ) as Outcome[];
};

/**
 * Checks if a child node's value should "count" toward its parent's evaluation
 * based on the propagateWhen constraint.
 * If propagateWhen is not set, the child always counts.
 * If propagateWhen is set, the child only counts if its value is in the list.
 */
const doesChildCount = (
  childValue: Outcome,
  propagateWhen: Outcome[] | string[] | undefined
): boolean => {
  const normalizedPropagateWhen = normalizePropagateWhen(propagateWhen);
  if (!normalizedPropagateWhen || normalizedPropagateWhen.length === 0) {
    return true; // No constraint, child always counts
  }
  return normalizedPropagateWhen.includes(childValue);
};

export const LogicalConnectiveNode = memo(
  ({
    id,
    data,
    isConnectable,
  }: {
    id: any;
    data: RFNodeData;
    isConnectable: any;
  }) => {
    const { updateNodeData } = useReactFlow();
    const [outcome, setOutcome] = useState<Outcome>(data.value);
    const logicalOperator = data.logicalOperator;

    const incomingConnections = useNodeConnections({
      handleType: "source",
    });
    const conditionIds = useMemo(
      () => incomingConnections.map(({ target }) => target),
      [incomingConnections]
    );
    const conditionNodeData = useNodesData<Node<RFNodeData>>(conditionIds);

    // Filter child values by propagateWhen constraint
    const countingChildValues: Outcome[] = useMemo(() => {
      const values: Outcome[] = [];
      conditionNodeData.forEach((node) => {
        const childValue = node.data.value;
        const propagateWhen = node.data.propagateWhen;
        // Only include child values that satisfy their propagateWhen constraint
        if (doesChildCount(childValue, propagateWhen)) {
          values.push(childValue);
        }
      });
      return values;
    }, [conditionNodeData]);

    useEffect(() => {
      const computeOutcome = (): Outcome => {
        // If no children count, the parent is TRUE (all conditions are satisfied or don't apply)
        if (countingChildValues.length === 0) {
          return Outcome.TRUE;
        }

        switch (logicalOperator) {
          case LogicalOperator.AND:
            return evaluateAnd(countingChildValues);
          case LogicalOperator.OR:
            return evaluateOr(countingChildValues);
          case LogicalOperator.NOT:
            return evaluateNot(countingChildValues);
          default:
            throw new Error(`Invalid logical operator: ${logicalOperator}`);
        }
      };

      const outputDecision = computeOutcome();
      setOutcome(outputDecision);
      updateNodeData(id, { value: outputDecision });
    }, [id, logicalOperator, countingChildValues, updateNodeData]);

    return (
      <BaseNode
        id={id}
        data={data}
        isConnectable={isConnectable}
        outcome={outcome}
      >
        <Paragraph
          color={outcome === Outcome.UNDECIDED ? undefined : "white"}
          className="font-semibold"
        >
          {data.question || data.label}
        </Paragraph>
      </BaseNode>
    );
  }
);
